<template>
  <div
    class="min-h-screen flex items-center justify-center dark:bg-gray-900 px-4"
    style="
      background-image: url('images/errorPage/error_bck_altgbz.png');
      background-size: cover;
      background-position: center;
    ">
    <div class="max-w-md w-full text-center space-y-8 animate-fade-in">
      <!-- Error Icon -->
      <div class="flex justify-center">
        <div class="rounded-full bg-red-100 dark:bg-red-900/20 p-6">
          <UIcon
            :name="errorMessages.icon"
            class="w-16 h-16 text-red-600 dark:text-red-400" />
        </div>
      </div>

      <!-- Error Code -->
      <div>
        <h1 class="text-6xl font-bold text-gray-900 dark:text-white mb-2">
          {{ error?.statusCode || 500 }}
        </h1>
        <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
          {{ errorMessages.title }}
        </h2>
        <p class="text-gray-600 dark:text-gray-400">
          {{ errorMessages.description }}
        </p>
      </div>

      <!-- Action Button -->
      <div class="pt-4">
        <UButton
          size="lg"
          color="primary"
          :label="errorMessages.btnLabel"
          icon="i-heroicons-home"
          @click="handleError" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { NuxtError } from '#app'

  const props = defineProps({
    'error': Object as () => NuxtError
  })

  const errorMessages = computed(() => {
    const statusCode = props.error?.statusCode || 500

    switch (statusCode) {
      case 401:
        return {
          'title': 'Unauthorized',
          'description': 'You need to be logged in to access this page.',
          'icon': 'i-heroicons-lock-closed',
          'btnLabel': 'Go to Login Page',
          'path': '/'
        }
      case 403:
        return {
          'title': 'Forbidden',
          'description': "You don't have permission to access this resource.",
          'icon': 'i-heroicons-shield-exclamation',
          'btnLabel': 'Go to Login Page',
          'path': '/'
        }
      case 404:
        return {
          'title': 'Page Not Found',
          'description': "The page you're looking for doesn't exist.",
          'icon': 'i-heroicons-question-mark-circle',
          'btnLabel': 'Go to Login Page',
          'path': '/'
        }
      case 500:
        return {
          'title': 'Server Error',
          'description': 'Something went wrong on our end. Please try again later.',
          'icon': 'i-heroicons-exclamation-triangle',
          'btnLabel': 'Go to Login Page',
          'path': '/'
        }
      default:
        return {
          'title': 'Error',
          'description': props.error?.statusMessage || 'An unexpected error occurred.',
          'icon': 'i-heroicons-exclamation-circle',
          'btnLabel': 'Go to Login Page',
          'path': '/'
        }
    }
  })

  function handleError() {
    clearError()
    navigateTo('/')
  }
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.5s ease-out;
  }
</style>
